import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, UserCheck, UserX, UserMinus, Wallet, Clock, IndianRupee,
  AlertCircle, CalendarCheck, TrendingUp, FileText, Zap, ArrowRight,
  Coins, HandCoins, CheckCircle2, Timer
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n) || 0);
const timeAgo = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const HRDashboard = () => {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/hr/dashboard`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="hr-dashboard-loading">
        <div className="text-teal-400 animate-pulse">Loading dashboard…</div>
      </div>
    );
  }

  const monthName = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  // Primary KPIs
  const kpis = [
    {
      label: "Total Employees", value: stats.total_employees, sub: `${stats.salary_configs} with salary config`,
      icon: Users, accent: "from-teal-500/20 to-teal-500/5", iconColor: "text-teal-300", border: "border-teal-500/40", testid: "kpi-total-employees",
    },
    {
      label: "Attendance Today", value: `${stats.attendance_pct}%`, sub: `${stats.today_present} present · ${stats.today_absent} absent`,
      icon: UserCheck, accent: "from-emerald-500/20 to-emerald-500/5", iconColor: "text-emerald-300", border: "border-emerald-500/40", testid: "kpi-attendance-today",
    },
    {
      label: "Payroll This Month", value: `₹${formatINR(stats.total_payroll_amount)}`, sub: `${stats.current_month_payroll} processed · ${stats.payroll_pending} pending`,
      icon: IndianRupee, accent: "from-amber-500/20 to-amber-500/5", iconColor: "text-amber-300", border: "border-amber-500/40", testid: "kpi-payroll-month",
    },
    {
      label: "Pending Advances", value: `₹${formatINR(stats.pending_advance_amount)}`, sub: `${stats.pending_advance_count} active · ${stats.pending_approval_count} need approval`,
      icon: HandCoins, accent: "from-purple-500/20 to-purple-500/5", iconColor: "text-purple-300", border: "border-purple-500/40", testid: "kpi-pending-advances",
    },
    {
      label: "Overtime Hours", value: stats.total_ot_hours, sub: `avg ${stats.avg_ot_hours} hrs / employee`,
      icon: Timer, accent: "from-rose-500/20 to-rose-500/5", iconColor: "text-rose-300", border: "border-rose-500/40", testid: "kpi-overtime",
    },
  ];

  // Attendance today pie
  const pieData = [
    { name: "Present", value: stats.today_present, color: "#34d399" },
    { name: "Half-day", value: stats.today_half, color: "#fbbf24" },
    { name: "Leave", value: stats.today_leave, color: "#60a5fa" },
    { name: "Absent", value: stats.today_absent, color: "#f87171" },
  ].filter(d => d.value > 0);

  const quickActions = [
    { label: "Mark Attendance", icon: CalendarCheck, to: "/hr/attendance", color: "text-emerald-300 hover:bg-emerald-500/10", testid: "qa-mark-attendance" },
    { label: "Generate Payroll", icon: Wallet, to: "/hr/payroll", color: "text-amber-300 hover:bg-amber-500/10", testid: "qa-generate-payroll" },
    { label: "New Advance", icon: Coins, to: "/hr/advances", color: "text-purple-300 hover:bg-purple-500/10", testid: "qa-new-advance" },
    { label: "Salary Config", icon: FileText, to: "/hr/salary-config", color: "text-cyan-300 hover:bg-cyan-500/10", testid: "qa-salary-config" },
  ];

  const statusColor = (s) => {
    if (s === "Paid") return "bg-green-500/20 text-green-300 border-green-500/40";
    if (s === "Draft") return "bg-amber-500/20 text-amber-300 border-amber-500/40";
    if (s === "Requested") return "bg-blue-500/20 text-blue-300 border-blue-500/40";
    if (s === "Pending") return "bg-amber-500/20 text-amber-300 border-amber-500/40";
    if (s === "Credited") return "bg-cyan-500/20 text-cyan-300 border-cyan-500/40";
    if (s === "Rejected") return "bg-red-500/20 text-red-300 border-red-500/40";
    if (s === "Fully Deducted") return "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
    if (s === "Partially Deducted") return "bg-purple-500/20 text-purple-300 border-purple-500/40";
    return "bg-gray-500/20 text-gray-300 border-gray-500/40";
  };

  return (
    <div className="space-y-6 pb-6" data-testid="hr-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-8 w-1 bg-gradient-to-b from-teal-400 to-emerald-400 rounded-full" />
            <h2 className="text-3xl font-bold text-teal-300 tracking-tight">HR Command Center</h2>
          </div>
          <p className="text-gray-400 text-sm mt-2 ml-3">Attendance · Payroll · Advances — snapshot for {monthName}</p>
        </div>
        {stats.pending_approval_count > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/40 rounded-lg text-amber-300 text-sm" data-testid="alert-pending-approval">
            <AlertCircle className="h-4 w-4" />
            <span>{stats.pending_approval_count} advance request(s) pending approval</span>
            <Button size="sm" variant="ghost" className="h-7 ml-2 text-amber-200 hover:text-amber-100" onClick={() => navigate("/hr/advances")} data-testid="btn-review-approvals">
              Review <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
        {kpis.map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className={`relative overflow-hidden bg-gradient-to-br ${k.accent} border ${k.border} transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-black/40`} data-testid={k.testid}>
              <CardContent className="p-4 relative">
                <div className="flex items-start justify-between">
                  <div className={`p-2 rounded-lg bg-black/30 ${k.iconColor}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{k.label}</div>
                  <div className={`text-2xl font-bold mt-1 ${k.iconColor}`}>{k.value}</div>
                  <div className="text-xs text-gray-400 mt-1 leading-tight">{k.sub}</div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Attendance trend */}
        <Card className="lg:col-span-2 bg-gray-900/50 border-gray-800" data-testid="attendance-trend-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-teal-400" /> Attendance Trend — Last 30 days
                </h3>
                <p className="text-xs text-gray-500 mt-1">Daily Present vs Absent breakdown</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={stats.attendance_trend}>
                <defs>
                  <linearGradient id="gradPresent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradAbsent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f87171" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#f87171" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={{ stroke: "#374151" }} />
                <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={{ stroke: "#374151" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#111827", border: "1px solid #374151", borderRadius: 8 }}
                  labelStyle={{ color: "#d1d5db" }}
                />
                <Area type="monotone" dataKey="present" stroke="#34d399" strokeWidth={2} fill="url(#gradPresent)" name="Present" />
                <Area type="monotone" dataKey="absent" stroke="#f87171" strokeWidth={2} fill="url(#gradAbsent)" name="Absent" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Today pie */}
        <Card className="bg-gray-900/50 border-gray-800" data-testid="today-breakdown-card">
          <CardContent className="p-5">
            <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-2">
              <CalendarCheck className="h-4 w-4 text-emerald-400" /> Today at a glance
            </h3>
            <p className="text-xs text-gray-500 mt-1 mb-3">Staff attendance for {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
            {pieData.length === 0 ? (
              <div className="h-56 flex flex-col items-center justify-center text-gray-500 text-sm">
                <UserMinus className="h-10 w-10 mb-2 opacity-30" />
                No attendance marked today
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {pieData.map((d, i) => <Cell key={i} fill={d.color} stroke="#111827" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: "#111827", border: "1px solid #374151", borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  {pieData.map(d => (
                    <div key={d.name} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: d.color }} />
                      <span className="text-gray-400">{d.name}</span>
                      <span className="text-gray-200 font-semibold ml-auto">{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom row: activity + quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent activity */}
        <Card className="lg:col-span-2 bg-gray-900/50 border-gray-800" data-testid="recent-activity-card">
          <CardContent className="p-5">
            <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-cyan-400" /> Recent Activity
            </h3>
            <p className="text-xs text-gray-500 mt-1 mb-4">Latest payroll & advance events</p>
            {stats.recent_activity.length === 0 ? (
              <div className="text-gray-500 text-sm py-8 text-center">No recent activity</div>
            ) : (
              <div className="space-y-2" data-testid="activity-list">
                {stats.recent_activity.map((a, i) => {
                  const Icon = a.type === "payroll" ? Wallet : HandCoins;
                  const iconColor = a.type === "payroll" ? "text-amber-300" : "text-purple-300";
                  const iconBg = a.type === "payroll" ? "bg-amber-500/10" : "bg-purple-500/10";
                  return (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-800/40 transition-colors">
                      <div className={`h-9 w-9 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-200 font-medium truncate">{a.title}</div>
                        <div className="text-xs text-gray-500 truncate">{a.subtitle}</div>
                      </div>
                      <Badge variant="outline" className={`${statusColor(a.status)} text-[10px] shrink-0`}>{a.status}</Badge>
                      <div className="text-[10px] text-gray-500 shrink-0 w-16 text-right">{timeAgo(a.time)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick actions + top OT */}
        <div className="space-y-4">
          <Card className="bg-gray-900/50 border-gray-800" data-testid="quick-actions-card">
            <CardContent className="p-5">
              <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-400" /> Quick Actions
              </h3>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {quickActions.map(q => {
                  const Icon = q.icon;
                  return (
                    <button
                      key={q.label}
                      onClick={() => navigate(q.to)}
                      data-testid={q.testid}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg bg-black/30 border border-gray-800 ${q.color} hover:border-gray-600 transition-all`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-xs font-medium">{q.label}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-gray-800" data-testid="top-ot-card">
            <CardContent className="p-5">
              <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-2">
                <Timer className="h-4 w-4 text-rose-400" /> Top Overtime ({monthName.split(" ")[0]})
              </h3>
              {stats.top_ot.length === 0 ? (
                <div className="text-gray-500 text-xs py-6 text-center">No overtime recorded yet</div>
              ) : (
                <div className="space-y-2 mt-3">
                  {stats.top_ot.map((e, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`top-ot-row-${i}`}>
                      <div className="h-7 w-7 rounded-full bg-rose-500/20 text-rose-300 text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-200 font-medium truncate">{e.name}</div>
                        <div className="text-[10px] text-gray-500">{e.hours} hrs · ₹{formatINR(e.pay)}</div>
                      </div>
                      <CheckCircle2 className="h-3 w-3 text-rose-400/60" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Payroll status strip */}
      {stats.current_month_payroll > 0 && (
        <Card className="bg-gradient-to-r from-gray-900/70 to-gray-900/40 border-gray-800" data-testid="payroll-status-strip">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10 text-amber-300">
                <Wallet className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm text-gray-300 font-semibold">Payroll Status — {monthName}</div>
                <div className="text-xs text-gray-500">Track disbursement progress</div>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <span className="text-gray-400">Paid</span>
                <span className="text-green-300 font-bold">{stats.payroll_paid}</span>
              </div>
              <div className="h-4 w-px bg-gray-700" />
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-400" />
                <span className="text-gray-400">Pending</span>
                <span className="text-amber-300 font-bold">{stats.payroll_pending}</span>
              </div>
              <div className="h-4 w-px bg-gray-700" />
              <div className="flex items-center gap-2">
                <UserX className="h-4 w-4 text-rose-400" />
                <span className="text-gray-400">Not generated</span>
                <span className="text-rose-300 font-bold">{Math.max(stats.total_employees - stats.current_month_payroll, 0)}</span>
              </div>
              <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10" onClick={() => navigate("/hr/payroll")} data-testid="btn-view-payroll">
                View Payroll <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HRDashboard;
